import { Empty, message, Table, TableProps, Tooltip } from "antd"
import { Key, TableRowSelection } from "antd/lib/table/interface"
import React, { FC, memo, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { NewBookingInputKeys } from "../../containers/CreateBooking/BookingSummary/BookingSummary"
import { AgeError, Attendee, Booker, BookerAttendeeInput, GetCourseDetailsByIdPublicQuery, Maybe, useCheckAttendeesAgeAtCourseStartQuery, useDeleteAttendeeMutation } from "../../generated/graphql"
import { URL_RESULTS } from "../../models/url"
import * as Sentry from "@sentry/react"
import { getGraphQLErrorMessage } from "../../helpers/sentry"
import { renderBirthdayAsAge, renderRestrictionsAttendeeTable } from "../TableColumnRenderers/TableColumnRenderers"
import css from "./SelectAttendeeTable.module.css"
import ActionButtons from "./ActionButtons"
import { collectCourseLessonIds } from "./useAttendeeEditLock"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { attendeeCheck } from "./checkAttendee"
import moment from "moment"
import { useTranslation } from "react-i18next"
import QueryErrorAlert from "../../components/QueryErrorAlert"

type SelectAttendeeTableProps = {
    booker: Booker
    loadingBooker: boolean
    refetchBooker: () => void
    attendees?: Maybe<Array<Attendee>>
    selectedAttendeeId?: Key
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    editAttendee: (attendee: BookerAttendeeInput) => void
    setSelectedAttendee: (attendeeId: string) => void
    courseId?: string
    setAttendeeAgeDoesFitCourse: (arg0: boolean) => void
    attendeeAgeDoesFitCourse: boolean
    isMemberOnlyCourse?: boolean
    isCourseWithPreRequisites?: boolean
    bookingRestriction: GetCourseDetailsByIdPublicQuery["courseByIdPublic"]["bookingRestriction"]
}

const SelectAttendeeTable: FC<SelectAttendeeTableProps> = ({
    booker,
    loadingBooker,
    refetchBooker,
    selectedAttendeeId,
    updateBookingData,
    editAttendee,
    setSelectedAttendee,
    courseId,
    setAttendeeAgeDoesFitCourse,
    attendeeAgeDoesFitCourse,
    isMemberOnlyCourse,
    isCourseWithPreRequisites,
    bookingRestriction,
}) => {
    const mediaSize = useMediaSize()
    const history = useHistory()
    const { t } = useTranslation()
    const [attendees, setAttendees] = useState<Maybe<Array<Attendee>>>([])
    const [deleteAttendeeById, { loading: deletingAttendee }] = useDeleteAttendeeMutation()

    // Einmal pro Booker berechnen (nicht pro Zeile im Zell-Renderer):
    // stabile Referenz, damit das memo() von ActionButtons greift.
    const allCourseLessonIds = React.useMemo(() => collectCourseLessonIds(booker?.attendees), [booker])

    // CHECK ATTENDEES AGE -------------------------------------------------------------------------------------------------------------
    const [selectedAttendeesAgeOk, setSelectedAttendeesAgeOk] = useState<boolean>(true)

    // error wird mitdestrukturiert: Schlägt die Altersprüfung fehl (Netzwerk/GraphQL),
    // blieb der Fehler zuvor komplett unsichtbar — loading wurde false, data blieb leer
    // und die Auswahl wirkte geprüft, obwohl keine Prüfung stattfand.
    const {
        data: attendeesAgeAtCourseStartData,
        loading: loadingAttendeesAgeAtStart,
        error: errorAttendeesAge,
        refetch: refetchAttendeesAge,
    } = useCheckAttendeesAgeAtCourseStartQuery({
        skip: !courseId || !selectedAttendeeId,
        variables: { courseId: courseId ?? "", attendeeId: (selectedAttendeeId as string) ?? "" },
    })

    const ageAtCourseStartData = attendeesAgeAtCourseStartData?.checkAttendeesAgeAtCourseStart

    // useEffect(() => {
    //     if (ageAtCourseStartData && ageAtCourseStartData.ageError !== undefined) {
    //         setSelectedAttendeesAgeOk(ageAtCourseStartData.ageError === null)
    //         setAttendeeAgeDoesFitCourse(ageAtCourseStartData.ageError === null)
    //     }
    // }, [ageAtCourseStartData, setAttendeeAgeDoesFitCourse, selectedAttendeeId])

    useEffect(() => {
        if (ageAtCourseStartData && ageAtCourseStartData.ageError !== undefined) {
            const ageFits = ageAtCourseStartData.ageError === null
            if (selectedAttendeesAgeOk !== ageFits) {
                setSelectedAttendeesAgeOk(ageFits)
                setAttendeeAgeDoesFitCourse(ageFits) // Only update if the value changes
            }
        }
    }, [ageAtCourseStartData, selectedAttendeesAgeOk, setAttendeeAgeDoesFitCourse])

    useEffect(() => {
        if (!courseId || !selectedAttendeeId || !refetchAttendeesAge) return
        setTimeout(() => {
            // refetchAttendeesAge({ attendeeId: selectedAttendeeId as string, courseId: courseId })
            // Rejection abfangen: Bei fehlgeschlagener Query bleibt der Fehler über
            // errorAttendeesAge sichtbar (keine unhandled rejection)
            refetchAttendeesAge().catch(() => undefined)
        }, 10)
    }, [courseId, refetchAttendeesAge, selectedAttendeeId])

    // Solange die Altersprüfung für den gewählten Teilnehmer lädt, gilt das Alter nicht als passend
    // (vorher war der Absenden-Knopf in dieser Zeit frei, W-28501).
    useEffect(() => {
        setAttendeeAgeDoesFitCourse(selectedAttendeesAgeOk && !loadingAttendeesAgeAtStart)
    }, [setAttendeeAgeDoesFitCourse, selectedAttendeesAgeOk, loadingAttendeesAgeAtStart])

    // IS ATTENDEE ALLOWED TO BOOK -------------------------------------------------------------------------------------------------------------
    const isAttendeeAllowedToBook = (attendee: BookerAttendeeInput) => {
        // allreadyBooked: boolean
        if (attendeeCheck(attendee, courseId, isCourseWithPreRequisites, bookingRestriction).allreadyBooked) {
            return false
        }
        // memberOnly: boolean
        if (isMemberOnlyCourse && !attendee.member) {
            return false
        }
        // bookedIntoPreRequisite: boolean — ohne Voraussetzung nur wählbar, wenn die Beschränkung die Warteliste erlaubt
        const checked = attendeeCheck(attendee, courseId, isCourseWithPreRequisites, bookingRestriction)
        if (isCourseWithPreRequisites && checked.bookedIntoPreRequisite === false && !checked.waitlistOnly) {
            return false
        }

        return true
    }

    // -----------------------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (booker.attendees) {
            const uniqueAttendees = <T,>(array: Array<T>): Array<T> => (array.length === 0 ? [] : Array.from(new Set(array)))

            setAttendees(uniqueAttendees(booker.attendees))
        }
    }, [booker])

    if (!attendees) {
        return <>No attendees found</>
    }

    const selectNextAttendee = async () => {
        const nextUnselectedAttendee = attendees?.find((attendee) => attendee.id !== selectedAttendeeId)

        if (nextUnselectedAttendee === undefined) {
            updateBookingData("attendeeId", attendees[0].id ?? undefined)
            setSelectedAttendee(attendees[0].id ?? "")
            return
        }

        updateBookingData("attendeeId", nextUnselectedAttendee.id)
        setSelectedAttendee(nextUnselectedAttendee.id)
    }

    const deleteAttendee = async (attendeeId: string) => {
        try {
            await deleteAttendeeById({ variables: { id: attendeeId } })
                .then((res) => {
                    message.success("Teilnehmer wurde gelöscht")
                    refetchBooker()
                })
                .catch((error: any) => {
                    message.error("Fehler beim Löschen des Teilnehmers")
                    console.log("error: ", error)
                    const rawErrorMessage = getGraphQLErrorMessage(error) || ""
                    if (rawErrorMessage.includes("attendee has bookings")) {
                        history.push(URL_RESULTS.replace(":resultType", "notDeletableHasBookings"))
                    } else {
                        Sentry.captureException(error)
                        history.push(URL_RESULTS.replace(":resultType", "unknown"))
                    }
                })
        } catch (error) {
            console.log("Error on deleteAttendeeById", error)
        }
    }

    const renderActionButtons = () => (value: unknown, record: BookerAttendeeInput, index: number) =>
        (
            <ActionButtons
                row={record}
                allCourseLessonIds={allCourseLessonIds}
                onDelete={async () => {
                    if (record.id === selectedAttendeeId) {
                        await selectNextAttendee()
                    }
                    deleteAttendee(record.id)
                }}
                onEdit={() => editAttendee(record)}
                booker={booker}
                deletingAttendee={deletingAttendee}
            />
        )

    // ROW SELECTION --------------------------------------------------------------------------------------------

    const selectedRowKeys = selectedAttendeeId === undefined ? [] : [selectedAttendeeId]

    const exclusiveMemberText = "Dieser Kurs ist exclusiv für Mitglieder buchbar"

    const rowSelectionSettings: TableRowSelection<BookerAttendeeInput> = {
        type: "radio",
        selectedRowKeys,
        onSelect: async (record, selected, selectedRows) => {
            const { id } = selectedRows[0] as BookerAttendeeInput
            if (id !== selectedAttendeeId) {
                // Check if the selected attendee is different
                setSelectedAttendee(id)
                updateBookingData("attendeeId", id)
                setAttendeeAgeDoesFitCourse(selectedAttendeesAgeOk)
            }
        },
        getCheckboxProps: (record) => ({
            disabled: isAttendeeAllowedToBook(record) === false,
        }),
        renderCell: (value, record, index, originNode) => {
            if (isAttendeeAllowedToBook(record) === false) {
                if (attendeeCheck(record, courseId, isCourseWithPreRequisites, bookingRestriction).allreadyBooked) {
                    return (
                        <Tooltip title='Dieser Teilnehmer ist bereits in diesem Kurs gebucht' placement='right'>
                            {originNode}
                        </Tooltip>
                    )
                }
                if (!selectedAttendeesAgeOk) {
                    switch (ageAtCourseStartData?.ageError) {
                        case AgeError.TooYoung:
                            return (
                                <Tooltip title='Der Teilnehmer ist zu jung für diesen Kurs' placement='right'>
                                    {originNode}
                                </Tooltip>
                            )
                        case AgeError.TooOld:
                            return (
                                <Tooltip title='Der Teilnehmer ist zu alt für diesen Kurs' placement='right'>
                                    {originNode}
                                </Tooltip>
                            )
                        default:
                            return originNode
                    }
                }
                if (isMemberOnlyCourse && !record.member) {
                    return (
                        <Tooltip title={exclusiveMemberText} placement='right' /* trigger={["click"]} */>
                            {originNode}
                        </Tooltip>
                    )
                }
                if (
                    isCourseWithPreRequisites &&
                    attendeeCheck(record, courseId, isCourseWithPreRequisites, bookingRestriction).bookedIntoPreRequisite === false &&
                    !attendeeCheck(record, courseId, isCourseWithPreRequisites, bookingRestriction).waitlistOnly
                ) {
                    return (
                        <Tooltip title='Dieser Teilnehmer ist nicht in den Voraussetzungskursen' placement='right' /* trigger={["click"]} */>
                            {originNode}
                        </Tooltip>
                    )
                }
            }
            return originNode
        },
        // ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    }

    if (attendees.length === 0) {
        return <Empty description='Keine Teilnehmer gefunden' imageStyle={{ display: "none" }} />
    }

    // RETURN -------------------------------------------------------------------------------------------------------------

    const tableProps: TableProps<BookerAttendeeInput> = {
        columns: [
            {
                title: "Vorname",
                dataIndex: "firstname",
            },
            {
                title: "Nachname",
                dataIndex: "lastname",
            },
            // {
            //     title: "Alter",
            //     dataIndex: "birthday",
            //     render: renderBirthdayAsAge,
            // },
            {
                title: "",
                dataIndex: "actions",
                render: renderActionButtons(),
                align: "right",
            },
        ],
    }

    if (mediaSize.media !== "sm") {
        // insert age column after lastname
        tableProps?.columns?.splice(2, 0, {
            title: "Alter",
            dataIndex: "birthday",
            render: renderBirthdayAsAge,
        })
    }

    if (isMemberOnlyCourse) {
        tableProps?.columns?.unshift({
            title: "Mitglied",
            dataIndex: "member",
            render: (member, record: BookerAttendeeInput) => renderRestrictionsAttendeeTable({ record, media: mediaSize.media }),
        })
    }

    const rowClassName = (record: BookerAttendeeInput): string => {
        const attendeeFits = isAttendeeAllowedToBook(record)

        return attendeeFits === false ? css.disabledRow : css.availableRow
    }

    const expandedRowRender = (record: BookerAttendeeInput) => {
        const getAgeValue = (age?: number): number => {
            if (!age) return 0

            if (age < 12) {
                return age
            } else if (age === 12) {
                return age
            } else if (age % 12 !== 0) {
                return age
            } else {
                return age / 12
            }
        }

        const minAgeAnniversary = moment(ageAtCourseStartData?.minAgeAnniversary).format("DD.MM.YYYY")
        const maxAgeAnniversary = moment(ageAtCourseStartData?.maxAgeAnniversary).format("DD.MM.YYYY")
        const minAge = ageAtCourseStartData?.minAge
        const maxAge = ageAtCourseStartData?.maxAge
        const maxAgeValue = getAgeValue(maxAge ?? undefined)
        const minAgeValue = getAgeValue(minAge ?? undefined)

        const getAgeUnit = (age?: number) => {
            if (!age) return ""
            if (age === 1) {
                return "Monat"
            } else if (age < 12) {
                return "Monaten"
            } else if (age === 12) {
                return "Jahr"
            } else if (age % 12 !== 0) {
                return "Monaten"
            } else {
                return "Jahren"
            }
        }
        const checkedAttendee = attendeeCheck(record, courseId, isCourseWithPreRequisites ?? false, bookingRestriction)
        if (!checkedAttendee) return null
        if (checkedAttendee.allreadyBooked) {
            // Fakt-Anzeige (kein Validierungsfehler): neutral/informativ gestylt, damit
            // sie schon vor dem ersten Submit-Versuch sichtbar sein darf, ohne wie ein
            // roter Fehler zu wirken.
            return (
                <p
                    style={{
                        color: "rgba(0, 0, 0, 0.65)",
                        background: "#f5f5f5",
                        textAlign: "center",
                    }}
                >
                    Dieser Teilnehmer ist bereits in diesem Kurs gebucht
                </p>
            )
        }
        if (!selectedAttendeesAgeOk) {
            switch (ageAtCourseStartData?.ageError) {
                case AgeError.TooYoung:
                    return (
                        <p
                            style={{
                                color: "red",
                                background: "rgba(255, 117, 117, 0.2)",
                                textAlign: "center",
                            }}
                        >
                            {`Dieser Teilnehmer ist zu jung für diesen Kurs. Das erforderliche Alter von ${minAgeValue} ${getAgeUnit(minAge ?? undefined)} erreicht er erst am ${minAgeAnniversary}.`}
                        </p>
                    )
                case AgeError.TooOld:
                    return (
                        <p
                            style={{
                                color: "red",
                                background: "rgba(255, 117, 117, 0.2)",
                                textAlign: "center",
                            }}
                        >
                            {`Dieser Teilnehmer ist zu alt für diesen Kurs. Das maximale Alter von ${maxAgeValue} ${getAgeUnit(maxAge ?? undefined)} hat er bereits am ${maxAgeAnniversary} erreicht.`}
                        </p>
                    )
                default:
                    return null
            }
        }
        if (isMemberOnlyCourse && checkedAttendee.isMember === false) {
            return (
                <p
                    style={{
                        color: "red",
                        background: "rgba(255, 117, 117, 0.2)",
                        textAlign: "center",
                    }}
                >
                    Dieser Teilnehmer ist kein Mitglied
                </p>
            )
        }
        if (isCourseWithPreRequisites && checkedAttendee.waitlistOnly) {
            return (
                <p
                    data-testid='attendee-prerequisite-waitlist-hint'
                    style={{
                        color: "#ad6800",
                        background: "#fffbe6",
                        textAlign: "center",
                    }}
                >
                    {t("bookingStepSummary.prerequisiteWaitlist.attendeeHint", "Voraussetzung fehlt, Eintrag nur auf die Warteliste möglich")}
                </p>
            )
        }
        if (isCourseWithPreRequisites && checkedAttendee.bookedIntoPreRequisite === false) {
            return (
                <p
                    style={{
                        color: "red",
                        background: "rgba(255, 117, 117, 0.2)",
                        textAlign: "center",
                    }}
                >
                    Dieser Teilnehmer ist nicht in den Voraussetzungskursen
                </p>
            )
        }
        if (record.id === selectedAttendeeId && !selectedAttendeesAgeOk) {
            switch (ageAtCourseStartData?.ageError) {
                case AgeError.TooYoung:
                    console.debug("ageError: ", ageAtCourseStartData?.ageError)
                    return (
                        <p
                            style={{
                                color: "red",
                                background: "rgba(255, 117, 117, 0.2)",
                                textAlign: "center",
                            }}
                        >
                            {`Dieser Teilnehmer ist zu jung für diesen Kurs. Das erforderliche Alter von ${minAge} ${getAgeUnit(minAge ?? undefined)} erreicht er erst am ${minAgeAnniversary}.`}
                        </p>
                    )
                case AgeError.TooOld:
                    return (
                        <p
                            style={{
                                color: "red",
                                background: "rgba(255, 117, 117, 0.2)",
                                textAlign: "center",
                            }}
                        >
                            {`Dieser Teilnehmer ist zu alt für diesen Kurs. Das maximale Alter von ${maxAge} ${getAgeUnit(maxAge ?? undefined)} hat er bereits am ${maxAgeAnniversary} erreicht.`}
                        </p>
                    )
                default:
                    return null
            }
        }
        return null
    }

    return (
        <>
            {/* Fehlgeschlagene Altersprüfung sichtbar machen: Das Alert erscheint OBERHALB
                der Tabelle (statt sie zu ersetzen), damit die Teilnehmerliste bedienbar bleibt. */}
            {errorAttendeesAge && (
                <QueryErrorAlert
                    testId='select-attendee-age-check-error'
                    style={{ marginBottom: 15 }}
                    message={t("selectAttendeeTable.loadErrorMessage")}
                    onRetry={refetchAttendeesAge}
                />
            )}
            <Table<BookerAttendeeInput>
                data-testid='select-attendee-table'
                rowClassName={(record) => rowClassName(record)}
                showHeader={false}
                loading={loadingBooker || loadingAttendeesAgeAtStart}
                columns={[
                    {
                        title: "Mitglied",
                        dataIndex: "member",
                        // render: (member) => (member ? "Mitglied" : "Nicht Mitglied"),
                        render: (member, record) => renderRestrictionsAttendeeTable({ record, media: mediaSize.media }),
                    },
                    {
                        title: "Vorname",
                        dataIndex: "firstname",
                    },
                    {
                        title: "Nachname",
                        dataIndex: "lastname",
                    },
                    {
                        title: "Alter",
                        dataIndex: "birthday",
                        render: renderBirthdayAsAge,
                    },
                    {
                        title: "",
                        dataIndex: "actions",
                        render: renderActionButtons(),
                        align: "right",
                    },
                ]}
                dataSource={attendees}
                rowSelection={rowSelectionSettings}
                rowKey='id'
                pagination={false}
                size='small'
                expandable={{
                    expandedRowRender: (record) => expandedRowRender(record),
                    expandedRowClassName: () => css.expandedRow,
                    expandedRowKeys: attendees?.map((attendee) => attendee.id),
                    rowExpandable: (record) => {
                        // "Bereits gebucht" ist ein auswahlunabhängiger Fakt und wird sofort
                        // beim Rendern unter dem Teilnehmer erklärt — nicht erst nachdem der
                        // Teilnehmer ausgewählt oder ein Submit versucht wurde.
                        if (attendeeCheck(record, courseId, isCourseWithPreRequisites, bookingRestriction).allreadyBooked) {
                            return true
                        }
                        // Altersprüfung hängt an der nur für den ausgewählten Teilnehmer
                        // geladenen Query, bleibt daher an die Auswahl gebunden.
                        if (rowSelectionSettings.selectedRowKeys?.includes(record.id)) {
                            return isAttendeeAllowedToBook(record) === false || !selectedAttendeesAgeOk
                        }

                        return false
                    },
                }}
                // @ts-ignore
                showExpandColumn={false}
                scroll={{ x: "100%" }}
            />
        </>
    )
}

export default memo(SelectAttendeeTable)
